import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useToast } from '../context/ToastContext';
import { API_CONFIG } from '../config/apiConfig';
import { FaUserAlt, FaBoxOpen, FaMapMarkerAlt, FaHeart, FaSignOutAlt, FaEdit, FaCheckCircle, FaExclamationCircle, FaSpinner, FaCamera, FaTrash, FaPlus, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import Breadcrumb from '../components/Breadcrumb';
import ShippingInfo from '../components/ShippingInfo';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, updateUserProfile, getUserOrders, getUserAddresses, addAddress, updateAddress, deleteAddress, updateUserPassword } from '../api/authApi';
import pureMeatAPI from '../api/pureMeatAPI';
const MEDIA_URL = API_CONFIG.IMAGE_URL;

function MyAccountPage() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const toast = useToast();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile: '',
    gender: '0'
  });
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({
    id: null,
    address: '',
    house: '',
    landmark: '',
    pincode: '',
    title: 'home',
    lat: '',
    lng: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    // Check URL params for tab and success message
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    const successParam = urlParams.get('order_success');
    
    if (tabParam && ['profile', 'orders', 'addresses'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    
    // Show success message if order was just created
    if (successParam === 'true') {
      toast.showSuccess('🎉 Payment successful! Your order has been created successfully.');
      
      // Clear the success param from URL
      const newUrl = window.location.pathname + (tabParam ? `?tab=${tabParam}` : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadProfile();
      if (activeTab === 'orders') loadOrders();
      if (activeTab === 'addresses') loadAddresses();
    }
  }, [user, activeTab]);

  const loadProfile = async () => {
    if (!user || !user.id) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const result = await getUserProfile(user.id, token);
      
      if (result.success) {
        setProfileData(result.data);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const loadOrders = async () => {
    if (!user || !user.id) return;
    
    setOrdersLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const result = await getUserOrders(user.id, token);
      
      if (result.success) {
        setOrders(result.data || []);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadAddresses = async () => {
    if (!user || !user.id) return;
    
    setAddressesLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const result = await getUserAddresses(user.id, token);
      
      if (result.success) {
        setAddresses(result.data || []);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
    } finally {
      setAddressesLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!profileData.first_name || !profileData.last_name || !profileData.mobile) {
      toast.showError('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      // Pure Meat expects these fields
      const updateData = {
        id: user.id,
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        email: profileData.email,
        mobile: profileData.mobile,
        country_code: '+91',
        type: 'user',
        gender: profileData.gender || 0,
        profile_image: profileData.profile_image || user.profile_image || '',
      };
      const result = await pureMeatAPI.updateUserProfile(updateData);
        if (result.status === 200) {
          toast.showSuccess('Profile updated successfully!');
          setEditMode(false);
          // Reload profile and update gender from backend response
          await loadProfile();
          if (result.data && result.data.gender !== undefined) {
            setProfileData(prev => ({ ...prev, gender: result.data.gender }));
          }
          // Update user context with new profile data
          if (result.data) {
            updateUser({
              ...user,
              first_name: result.data.first_name || profileData.first_name,
              last_name: result.data.last_name || profileData.last_name,
              mobile: result.data.mobile || profileData.mobile,
              email: result.data.email || profileData.email,
              gender: result.data.gender || profileData.gender,
              cover: result.data.cover || profileData.cover
            });
          }
        } else {
          toast.showError(result.message || 'Failed to update profile');
          console.error('Profile update failed:', result);
        }
    } catch (error) {
      toast.showError(error.message || 'Network error. Please try again.');
      console.error('Profile update error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    const token = localStorage.getItem('auth_token');
    // MEDIA_URL is already defined at the top
    import('../api/authApi').then(({ uploadProfileImage }) => {
      uploadProfileImage(file, token).then(async (result) => {
        if (result.success && result.image_name) {
          // Pure Meat expects only id and cover for image update
          const updateData = {
            id: user.id,
            cover: result.image_name
          };
          const updateResult = await pureMeatAPI.updateUserProfile(updateData);
          if (updateResult.status === 200) {
            toast.showSuccess('Profile image updated successfully!');
            user.cover = `${MEDIA_URL}/${result.image_name}`;
            setProfileData(prev => ({ ...prev, cover: result.image_name }));
            await loadProfile();
          } else {
            toast.showError(updateResult.message || 'Failed to update profile image in backend');
          }
        } else {
          toast.showError(result.message || 'Failed to upload image');
        }
        setLoading(false);
      }).catch((error) => {
        toast.showError(error.message || 'Network error. Please try again.');
        setLoading(false);
      });
    });
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const addressData = {
        ...addressForm,
        uid: user.id
      };
      // Remove id for add operation
      if (!addressForm.id) {
        delete addressData.id;
      }
      
      let result;
      if (addressForm.id) {
        result = await updateAddress(addressData, token);
      } else {
        result = await addAddress(addressData, token);
      }
      
      if (result.success) {
        toast.showSuccess(`Address ${addressForm.id ? 'updated' : 'added'} successfully!`);
        setShowAddressForm(false);
        await loadAddresses();
      } else {
        toast.showError(result.message || 'Failed to save address');
      }
    } catch (error) {
      toast.showError('Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAddress = (addr) => {
    setAddressForm(addr);
    setShowAddressForm(true);
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const result = await deleteAddress(id, token);
      
      if (result.success) {
        toast.showSuccess('Address deleted successfully!');
        await loadAddresses();
      } else {
        toast.showError(result.message || 'Failed to delete address');
      }
    } catch (error) {
      toast.showError('Failed to delete address');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.showError('Please fill all password fields');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.showError('New password must be at least 6 characters');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.showError('New password and confirm password do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const result = await updateUserPassword(user.id, passwordData.currentPassword, passwordData.newPassword, token);

      if (result.success) {
        toast.showSuccess('Password updated successfully!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordForm(false);
      } else {
        toast.showError(result.message || 'Failed to update password');
      }
    } catch (error) {
      toast.showError(error.message || 'Network error. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const getProfileImageUrl = () => {
    // Use cover field and prepend MEDIA_URL for Pure Meat compatibility
    if (profileData.cover) {
      return `${MEDIA_URL}/${profileData.cover}`;
    }
    return '/assets/img/other/account-thumb.webp';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  const getOrderStatus = (statusData) => {
    try {
      // If status is a JSON string, parse it
      if (typeof statusData === 'string' && statusData.startsWith('[')) {
        const statusArray = JSON.parse(statusData);
        if (statusArray && statusArray.length > 0) {
          const status = statusArray[0].status;
          return getStatusDisplay(status);
        }
      }
      // If it's already an object or simple string
      return getStatusDisplay(statusData);
    } catch (error) {
      return { text: 'Pending', color: 'bg-warning' };
    }
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'created': { text: 'Created', color: 'bg-info' },
      'accepted': { text: 'Accepted', color: 'bg-primary' },
      'confirmed': { text: 'Confirmed', color: 'bg-primary' },
      'preparing': { text: 'Preparing', color: 'bg-warning' },
      'ongoing': { text: 'On the way', color: 'bg-warning' },
      'delivered': { text: 'Delivered', color: 'bg-success' },
      'completed': { text: 'Completed', color: 'bg-success' },
      'cancelled': { text: 'Cancelled', color: 'bg-danger' },
      'rejected': { text: 'Rejected', color: 'bg-danger' }
    };
    return statusMap[status] || { text: status || 'Pending', color: 'bg-secondary' };
  };

  return (
    <>
      <Header />
      <main className="main__content_wrapper">
        <Breadcrumb title="My Account" items={[{ label: 'Home', href: '/' }, { label: 'My Account' }]} />
        
        <section className="my__account--section section--padding">
          <div className="container">
            
            <div className="card shadow-sm mb-4 p-4" style={{ borderRadius: '15px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
              <div className="d-flex align-items-center gap-3 text-white">
                <div className="position-relative">
                  <img 
                    src={getProfileImageUrl()} 
                    alt="Profile" 
                    style={{ 
                      width: '70px', 
                      height: '70px', 
                      borderRadius: '50%', 
                      objectFit: 'cover',
                      border: '3px solid white',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                    }} 
                  />
                </div>
                <div>
                  <h4 className="mb-1" style={{ fontWeight: 600 }}>Welcome back, {user?.first_name || 'User'}! </h4>
                  <p className="mb-0" style={{ opacity: 0.9, fontSize: '14px' }}>{user?.email}</p>
                </div>
              </div>
            </div>
            
            <div className="my__account--section__inner d-flex flex-wrap flex-md-nowrap gap-4">
              <aside className="account__left--sidebar card shadow-sm p-0" style={{ minWidth: '280px', maxWidth: '280px', borderRadius: '15px', border: 'none', position: 'sticky', top: '100px', height: 'fit-content' }}>
                <div className="p-4 border-bottom">
                  <h5 className="mb-0 d-flex align-items-center gap-2" style={{ fontWeight: 600, color: '#2d3748' }}>
                    <FaUserAlt style={{ color: '#667eea' }} />
                    My Account
                  </h5>
                </div>
                <ul className="account__menu list-unstyled mb-0">
                  <li className={`account__menu--list ${activeTab === 'profile' ? 'active' : ''}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <a 
                      href="#" 
                      onClick={(e) => { e.preventDefault(); setActiveTab('profile'); }}
                      className="d-flex align-items-center gap-3 p-3 text-decoration-none"
                      style={{ 
                        color: activeTab === 'profile' ? '#667eea' : '#4a5568',
                        background: activeTab === 'profile' ? '#f7fafc' : 'transparent',
                        fontWeight: activeTab === 'profile' ? 600 : 400,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <FaUserAlt size={18} />
                      <span>Profile</span>
                    </a>
                  </li>
                  <li className={`account__menu--list ${activeTab === 'orders' ? 'active' : ''}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <a 
                      href="#" 
                      onClick={(e) => { e.preventDefault(); setActiveTab('orders'); }}
                      className="d-flex align-items-center gap-3 p-3 text-decoration-none"
                      style={{ 
                        color: activeTab === 'orders' ? '#667eea' : '#4a5568',
                        background: activeTab === 'orders' ? '#f7fafc' : 'transparent',
                        fontWeight: activeTab === 'orders' ? 600 : 400,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <FaBoxOpen size={18} />
                      <span>Orders</span>
                    </a>
                  </li>
                  <li className={`account__menu--list ${activeTab === 'addresses' ? 'active' : ''}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <a 
                      href="#" 
                      onClick={(e) => { e.preventDefault(); setActiveTab('addresses'); }}
                      className="d-flex align-items-center gap-3 p-3 text-decoration-none"
                      style={{ 
                        color: activeTab === 'addresses' ? '#667eea' : '#4a5568',
                        background: activeTab === 'addresses' ? '#f7fafc' : 'transparent',
                        fontWeight: activeTab === 'addresses' ? 600 : 400,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <FaMapMarkerAlt size={18} />
                      <span>Addresses</span>
                    </a>
                  </li>
                  <li className={`account__menu--list ${activeTab === 'password' ? 'active' : ''}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <a 
                      href="#" 
                      onClick={(e) => { e.preventDefault(); setActiveTab('password'); }}
                      className="d-flex align-items-center gap-3 p-3 text-decoration-none"
                      style={{ 
                        color: activeTab === 'password' ? '#667eea' : '#4a5568',
                        background: activeTab === 'password' ? '#f7fafc' : 'transparent',
                        fontWeight: activeTab === 'password' ? 600 : 400,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <FaLock size={18} />
                      <span>Change Password</span>
                    </a>
                  </li>
                  <li className="account__menu--list" style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <a 
                      href="/wishlist"
                      className="d-flex align-items-center gap-3 p-3 text-decoration-none"
                      style={{ color: '#4a5568', transition: 'all 0.3s ease' }}
                    >
                      <FaHeart size={18} />
                      <span>Wishlist</span>
                    </a>
                  </li>
                  <li className="account__menu--list">
                    <a 
                      href="#" 
                      onClick={(e) => { e.preventDefault(); logout(); navigate('/'); }}
                      className="d-flex align-items-center gap-3 p-3 text-decoration-none"
                      style={{ color: '#e53e3e', transition: 'all 0.3s ease' }}
                    >
                      <FaSignOutAlt size={18} />
                      <span>Log Out</span>
                    </a>
                  </li>
                </ul>
              </aside>
              
              <div className="account__wrapper flex-grow-1">
                {/* Profile Tab */}
                {activeTab === 'profile' && (
                  <div className="account__content card shadow-sm p-4" style={{ borderRadius: '15px', border: 'none' }}>
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <h3 className="mb-0 d-flex align-items-center gap-2" style={{ fontWeight: 600, color: '#2d3748' }}>
                        <FaUserAlt style={{ color: '#667eea' }} />
                        Profile Information
                      </h3>
                    </div>
                    
                    <div className="mb-5 text-center">
                      <div className="position-relative d-inline-block">
                        <img 
                          src={getProfileImageUrl()} 
                          alt="Profile" 
                          style={{ 
                            width: '140px', 
                            height: '140px', 
                            borderRadius: '50%', 
                            objectFit: 'cover',
                            border: '4px solid #667eea',
                            boxShadow: '0 8px 16px rgba(102, 126, 234, 0.2)'
                          }} 
                        />
                        <label 
                          htmlFor="profileImageInput" 
                          style={{ 
                            position: 'absolute', 
                            bottom: '5px', 
                            right: '5px', 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                            color: 'white', 
                            borderRadius: '50%', 
                            width: '42px', 
                            height: '42px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            cursor: 'pointer',
                            border: '3px solid white',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                            transition: 'transform 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <FaCamera size={18} />
                        </label>
                        <input 
                          type="file" 
                          id="profileImageInput" 
                          accept="image/*" 
                          onChange={handleImageUpload} 
                          style={{ display: 'none' }} 
                        />
                      </div>
                    </div>

                    {!editMode ? (
                      <div>
                        <div className="row g-4 mb-4">
                          <div className="col-md-6">
                            <div className="p-4" style={{ background: '#f7fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                              <label className="text-muted mb-2" style={{ fontSize: '13px', fontWeight: 500 }}>Full Name</label>
                              <p className="mb-0" style={{ fontSize: '16px', fontWeight: 600, color: '#2d3748' }}>{profileData.first_name} {profileData.last_name}</p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="p-4" style={{ background: '#f7fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                              <label className="text-muted mb-2" style={{ fontSize: '13px', fontWeight: 500 }}>Email Address</label>
                              <p className="mb-0" style={{ fontSize: '16px', fontWeight: 600, color: '#2d3748' }}>{profileData.email}</p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="p-4" style={{ background: '#f7fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                              <label className="text-muted mb-2" style={{ fontSize: '13px', fontWeight: 500 }}>Mobile Number</label>
                              <p className="mb-0" style={{ fontSize: '16px', fontWeight: 600, color: '#2d3748' }}>{profileData.mobile || 'Not provided'}</p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="p-4" style={{ background: '#f7fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                              <label className="text-muted mb-2" style={{ fontSize: '13px', fontWeight: 500 }}>Gender</label>
                              <p className="mb-0" style={{ fontSize: '16px', fontWeight: 600, color: '#2d3748' }}>{profileData.gender === 1 ? 'Male' : profileData.gender === 2 ? 'Female' : 'Other'}</p>
                            </div>
                          </div>
                        </div>
                        <button 
                          className="continue__shipping--btn primary__btn d-flex align-items-center gap-2" 
                          onClick={() => setEditMode(true)}
                          style={{ borderRadius: '10px', padding: '12px 24px', fontWeight: 600 }}
                        >
                          <FaEdit />
                          Edit Profile
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleProfileUpdate}>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="checkout__input--label">First Name <span className="checkout__input--label__star">*</span></label>
                            <input 
                              className="checkout__input--field border-radius-5" 
                              type="text" 
                              value={profileData.first_name}
                              onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="checkout__input--label">Last Name <span className="checkout__input--label__star">*</span></label>
                            <input 
                              className="checkout__input--field border-radius-5" 
                              type="text" 
                              value={profileData.last_name}
                              onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
                              required
                            />
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="checkout__input--label">Mobile <span className="checkout__input--label__star">*</span></label>
                            <input 
                              className="checkout__input--field border-radius-5" 
                              type="tel" 
                              value={profileData.mobile}
                              onChange={(e) => setProfileData({...profileData, mobile: e.target.value})}
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="checkout__input--label">Gender</label>
                            <select 
                              className="checkout__input--field border-radius-5" 
                              value={parseInt(profileData.gender) || 0}
                              onChange={(e) => setProfileData({...profileData, gender: parseInt(e.target.value)})}
                            >
                              <option value={0}>Other</option>
                              <option value={1}>Male</option>
                              <option value={2}>Female</option>
                            </select>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <button 
                            type="submit" 
                            className="continue__shipping--btn primary__btn border-radius-5"
                            disabled={loading}
                          >
                            {loading ? 'Updating...' : 'Update Profile'}
                          </button>
                          <button 
                            type="button" 
                            className="continue__shipping--btn btn-secondary border-radius-5"
                            onClick={() => { setEditMode(false); loadProfile(); }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {/* Orders Tab */}
                {activeTab === 'orders' && (
                  <div className="account__content card shadow-sm p-4" style={{ borderRadius: '15px', border: 'none' }}>
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <h3 className="mb-0 d-flex align-items-center gap-2" style={{ fontWeight: 600, color: '#2d3748' }}>
                        <FaBoxOpen style={{ color: '#667eea' }} />
                        Orders History
                      </h3>
                    </div>
                    {ordersLoading ? (
                      <p>Loading orders...</p>
                    ) : orders.length === 0 ? (
                      <p>No orders found</p>
                    ) : (
                      <div className="account__table--area">
                        <table className="account__table">
                          <thead className="account__table--header">
                            <tr className="account__table--header__child">
                              <th className="account__table--header__child--items">Order ID</th>
                              <th className="account__table--header__child--items">Date</th>
                              <th className="account__table--header__child--items">Status</th>
                              <th className="account__table--header__child--items">Total</th>
                              <th className="account__table--header__child--items">Action</th>
                            </tr>
                          </thead>
                          <tbody className="account__table--body">
                            {orders.map((order) => (
                              <tr key={order.id} className="account__table--body__child">
                                <td className="account__table--body__child--items">#{order.id}</td>
                                <td className="account__table--body__child--items">{formatDate(order.date_time || order.created_at)}</td>
                                <td className="account__table--body__child--items">
                                  <span className={`badge ${getOrderStatus(order.status).color}`}>
                                    {getOrderStatus(order.status).text}
                                  </span>
                                </td>
                                <td className="account__table--body__child--items">${order.grand_total}</td>
                                <td className="account__table--body__child--items">
                                  <button 
                                    className="btn btn-sm btn-primary"
                                    onClick={() => navigate(`/order/${order.id}`)}
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Addresses Tab */}
                {activeTab === 'addresses' && (
                  <div className="account__content card shadow-sm p-4" style={{ borderRadius: '15px', border: 'none' }}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h3 className="mb-0 d-flex align-items-center gap-2" style={{ fontWeight: 600, color: '#2d3748' }}>
                        <FaMapMarkerAlt style={{ color: '#667eea' }} />
                        My Addresses
                      </h3>
                      <button 
                        className="continue__shipping--btn primary__btn d-flex align-items-center gap-2"
                        onClick={() => {
                          setAddressForm({
                            id: null,
                            address: '',
                            house: '',
                            landmark: '',
                            pincode: '',
                            title: 'home',
                            lat: '0',
                            lng: '0'
                          });
                          setShowAddressForm(true);
                        }}
                        style={{ borderRadius: '10px', padding: '10px 20px', fontWeight: 600 }}
                      >
                        <FaPlus size={16} />
                        Add New Address
                      </button>
                    </div>

                    {showAddressForm && (
                      <div className="mb-4 p-4" style={{ background: '#f7fafc', borderRadius: '12px', border: '2px dashed #cbd5e0' }}>
                        <h5 className="mb-4 d-flex align-items-center gap-2" style={{ fontWeight: 600, color: '#2d3748' }}>
                          {addressForm.id ? <FaEdit style={{ color: '#667eea' }} size={16} /> : <FaPlus style={{ color: '#667eea' }} size={16} />}
                          {addressForm.id ? 'Edit Address' : 'Add New Address'}
                        </h5>
                        <form onSubmit={handleAddressSubmit}>
                          <div className="row mb-3">
                            <div className="col-md-12">
                              <label className="checkout__input--label">Address <span className="checkout__input--label__star">*</span></label>
                              <input 
                                className="checkout__input--field border-radius-5" 
                                type="text" 
                                value={addressForm.address}
                                onChange={(e) => setAddressForm({...addressForm, address: e.target.value})}
                                required
                              />
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <label className="checkout__input--label">House/Flat No <span className="checkout__input--label__star">*</span></label>
                              <input 
                                className="checkout__input--field border-radius-5" 
                                type="text" 
                                value={addressForm.house}
                                onChange={(e) => setAddressForm({...addressForm, house: e.target.value})}
                                required
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="checkout__input--label">Landmark <span className="checkout__input--label__star">*</span></label>
                              <input 
                                className="checkout__input--field border-radius-5" 
                                type="text" 
                                value={addressForm.landmark}
                                onChange={(e) => setAddressForm({...addressForm, landmark: e.target.value})}
                                required
                              />
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <label className="checkout__input--label">Pincode <span className="checkout__input--label__star">*</span></label>
                              <input 
                                className="checkout__input--field border-radius-5" 
                                type="text" 
                                value={addressForm.pincode}
                                onChange={(e) => setAddressForm({...addressForm, pincode: e.target.value})}
                                required
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="checkout__input--label">Title</label>
                              <select 
                                className="checkout__input--field border-radius-5" 
                                value={addressForm.title}
                                onChange={(e) => setAddressForm({...addressForm, title: e.target.value})}
                              >
                                <option value="home">Home</option>
                                <option value="work">Work</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <button 
                              type="submit" 
                              className="continue__shipping--btn primary__btn border-radius-5"
                              disabled={loading}
                            >
                              {loading ? 'Saving...' : addressForm.id ? 'Update Address' : 'Add Address'}
                            </button>
                            <button 
                              type="button" 
                              className="continue__shipping--btn btn-secondary border-radius-5"
                              onClick={() => setShowAddressForm(false)}
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    {addressesLoading ? (
                      <p>Loading addresses...</p>
                    ) : addresses.length === 0 ? (
                      <p>No addresses found</p>
                    ) : (
                      <div className="row g-4">
                        {addresses.map((addr) => (
                          <div key={addr.id} className="col-md-6">
                            <div className="p-4 h-100 position-relative" style={{ 
                              border: '2px solid #e2e8f0', 
                              borderRadius: '12px',
                              transition: 'all 0.3s ease',
                              background: '#fff'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#667eea';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#e2e8f0';
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}>
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="d-flex align-items-center gap-2">
                                  <FaMapMarkerAlt style={{ color: '#667eea' }} />
                                  <h6 className="mb-0 text-capitalize" style={{ fontWeight: 600, color: '#2d3748' }}>{addr.title}</h6>
                                </div>
                                <div className="d-flex gap-3">
                                  <FaEdit 
                                    size={16} 
                                    style={{ color: '#667eea', cursor: 'pointer' }} 
                                    onClick={() => handleEditAddress(addr)}
                                    title="Edit"
                                  />
                                  <FaTrash 
                                    size={16} 
                                    style={{ color: '#e53e3e', cursor: 'pointer' }} 
                                    onClick={() => handleDeleteAddress(addr.id)}
                                    title="Delete"
                                  />
                                </div>
                              </div>
                              <div style={{ color: '#4a5568', fontSize: '14px', lineHeight: '1.6' }}>
                                <p className="mb-2"><strong style={{ color: '#2d3748' }}>{addr.house}</strong>, {addr.landmark}</p>
                                <p className="mb-2">{addr.address}</p>
                                <p className="mb-0"><strong style={{ color: '#2d3748' }}>Pincode:</strong> {addr.pincode}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Password Tab */}
                {activeTab === 'password' && (
                  <div className="account__content card shadow-sm p-4" style={{ borderRadius: '15px', border: 'none' }}>
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <h3 className="mb-0 d-flex align-items-center gap-2" style={{ fontWeight: 600, color: '#2d3748' }}>
                        <FaLock style={{ color: '#667eea' }} />
                        Change Password
                      </h3>
                    </div>

                    <form onSubmit={handlePasswordSubmit} style={{ maxWidth: '500px' }}>
                        <div className="mb-4">
                          <label className="checkout__input--label d-flex align-items-center gap-2">
                            <FaLock style={{ color: '#667eea', fontSize: '16px' }} />
                            Current Password
                          </label>
                          <div style={{ position: 'relative' }}>
                            <input 
                              type={showCurrentPassword ? 'text' : 'password'}
                              className="checkout__input--field border-radius-5" 
                              placeholder="Enter your current password"
                              value={passwordData.currentPassword}
                              onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                              required
                              name="current-password"
                              autoComplete="current-password"
                              style={{ paddingRight: '40px' }}
                            />
                            <button
                              type="button"
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              style={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#667eea',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              {showCurrentPassword ? <FaEye /> : <FaEyeSlash />}
                            </button>
                          </div>
                        </div>

                        <div className="mb-4">
                          <label className="checkout__input--label d-flex align-items-center gap-2">
                            <FaLock style={{ color: '#667eea', fontSize: '16px' }} />
                            New Password
                          </label>
                          <div style={{ position: 'relative' }}>
                            <input 
                              type={showNewPassword ? 'text' : 'password'}
                              className="checkout__input--field border-radius-5" 
                              placeholder="Enter new password (min 6 characters)"
                              value={passwordData.newPassword}
                              onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                              required
                              minLength="6"
                              name="new-password"
                              autoComplete="new-password"
                              style={{ paddingRight: '40px' }}
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              style={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#667eea',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              {showNewPassword ? <FaEye /> : <FaEyeSlash />}
                            </button>
                          </div>
                        </div>

                        <div className="mb-4">
                          <label className="checkout__input--label d-flex align-items-center gap-2">
                            <FaLock style={{ color: '#667eea', fontSize: '16px' }} />
                            Confirm New Password
                          </label>
                          <div style={{ position: 'relative' }}>
                            <input 
                              type={showConfirmPassword ? 'text' : 'password'}
                              className="checkout__input--field border-radius-5" 
                              placeholder="Confirm new password"
                              value={passwordData.confirmPassword}
                              onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                              required
                              minLength="6"
                              name="confirm-password"
                              autoComplete="new-password"
                              style={{ paddingRight: '40px' }}
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              style={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#667eea',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                            </button>
                          </div>
                        </div>

                        <div className="d-flex gap-2">
                          <button 
                            type="submit" 
                            className="primary__btn border-radius-5"
                            disabled={passwordLoading}
                          >
                            {passwordLoading ? 'Updating...' : 'Update Password'}
                          </button>
                          <button 
                            type="button" 
                            className="btn-secondary border-radius-5"
                            onClick={() => {
                              setShowPasswordForm(false);
                              setPasswordData({
                                currentPassword: '',
                                newPassword: '',
                                confirmPassword: ''
                              });
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </section>

        <ShippingInfo />
      </main>

      <Footer />
    </>
  );
}

export default MyAccountPage;
